/**
 * Rozepsaný import v průvodci Codable — kde jsem skončil.
 *
 * Proč (Filip 2026-09-13): když člověk na kroku PREREKVIZITY zjistí, že nemá
 * právo zápisu, odešle žádost ke schválení a z průvodce odejde. Po schválení se
 * vrátil na prázdný seznam projektů a musel znovu hledat, co si vybral a jaký
 * mód importu zvolil. Drží se to v localStorage prohlížeče, ne na serveru —
 * je to rozdělaná volba v UI, ne stav projektu.
 *
 * Per projekt, ne jeden globální slot: člověk může mít rozjednané víc importů
 * najednou a mód patří ke konkrétnímu repozitáři.
 */
export const IMPORT_RESUME_STORAGE_KEY = 'acm_codable_import_resume';

/** Kolik rozdělaných importů si pamatovat; starší vypadnou. */
const MAX_ENTRIES = 10;

/** Po měsíci je „rozdělaný import" spíš smetí než nápověda. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface ImportResumeEntry<P = unknown> {
  /** Stabilní klíč projektu — spojení + id/slug. */
  key: string;
  /** Vybraný projekt tak, jak ho průvodce potřebuje zobrazit. */
  project: P;
  /** Zvolený mód importu (`existing` | `clone` | `fork`). */
  mode: string;
  /** ISO čas poslední změny; podle něj se zapomíná. */
  saved_at: string;
  /** Čeká se na schválení práv? Kvůli hlášce po návratu. */
  pending_approval?: boolean;
}

interface ImportResumeStore {
  last?: string;
  items: Record<string, ImportResumeEntry>;
}

/** Klíč je spojení + projekt — stejný projekt na jiném GitLabu je jiná věc. */
export function importResumeKey(
  connection: string | undefined | null,
  project: number | string,
): string {
  return `${connection ?? 'gl'}:${project}`;
}

function readStore(): ImportResumeStore {
  if (typeof window === 'undefined') return { items: {} };
  try {
    const raw = window.localStorage.getItem(IMPORT_RESUME_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (
      !parsed || typeof parsed !== 'object' || typeof parsed.items !== 'object'
    ) {
      return { items: {} };
    }
    return {
      last: typeof parsed.last === 'string' ? parsed.last : undefined,
      items: parsed.items,
    };
  } catch {
    // Rozbitý nebo cizí obsah klíče nesmí shodit průvodce.
    return { items: {} };
  }
}

function writeStore(store: ImportResumeStore): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      IMPORT_RESUME_STORAGE_KEY,
      JSON.stringify(store),
    );
  } catch {
    /* plná nebo zakázaná storage — rozdělaný import není kritický stav */
  }
}

/** Čistá podoba pro testy i pro runtime: co z uloženého stavu ještě platí. */
export function pruneImportResume(
  store: ImportResumeStore,
  now = Date.now(),
): ImportResumeStore {
  const fresh = Object.entries(store.items ?? {})
    .filter(([, entry]) => {
      const at = Date.parse(entry?.saved_at ?? '');
      return Number.isFinite(at) && now - at < MAX_AGE_MS;
    })
    .sort(([, a], [, b]) => Date.parse(b.saved_at) - Date.parse(a.saved_at))
    .slice(0, MAX_ENTRIES);
  const items = Object.fromEntries(fresh);
  const last = store.last && items[store.last] ? store.last : fresh[0]?.[0];
  return { ...(last ? { last } : {}), items };
}

/** Zapamatuj si výběr projektu a mód; poslední zápis určuje, kam se vrátíme. */
export function rememberImport<P>(entry: ImportResumeEntry<P>): void {
  const store = pruneImportResume(readStore());
  const previous = store.items[entry.key];
  store.items[entry.key] = {
    ...entry,
    // Čekání na schválení se nesmí ztratit tím, že člověk mezitím přepnul mód
    // — jinak by se průvodce po návratu neotevřel tam, kde skončil.
    pending_approval: entry.pending_approval ?? previous?.pending_approval,
  } as ImportResumeEntry;
  store.last = entry.key;
  writeStore(pruneImportResume(store));
}

/** Kam se průvodce vrátí — poslední rozdělaný import, nebo nic. */
export function lastImportResume<P>(
  now = Date.now(),
): ImportResumeEntry<P> | null {
  const store = pruneImportResume(readStore(), now);
  const key = store.last;
  return key ? (store.items[key] as ImportResumeEntry<P>) ?? null : null;
}

/** Hotovo nebo zavřeno — ať se to příště nenabízí. */
export function forgetImport(key: string): void {
  const store = pruneImportResume(readStore());
  delete store.items[key];
  if (store.last === key) delete store.last;
  writeStore(pruneImportResume(store));
}
